import React, { useState, useEffect, useRef } from "react";
import { useChat } from "../../hooks/chat/useChat";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { chatService } from "../../services/ChatService";
import { MessageList } from "./shared/MessageList";
import { TicketForm } from "./shared/TicketForm";
import {
  ChatIcon,
  MinimizeIcon,
  MaximizeIcon,
  CloseIcon,
  AttachmentIcon,
} from "./shared/ChatIcons";
import {
  PaperAirplaneIcon,
  XCircleIcon,
  XMarkIcon,
  ArrowRightOnRectangleIcon,
} from "@heroicons/react/24/outline";
import { socketService } from "@/api/socket";
import { ChatMessage } from "@/types/chat";
import styles from "./GuestChatBox.module.css";

const CLOSE_FADE_MS = 400;

export default function GuestChatBox() {
  const queryClient = useQueryClient();
  const [showTicketForm, setShowTicketForm] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
  });

  const {
    isOpen,
    setIsOpen,
    isMinimized,
    setIsMinimized,
    newMessage,
    setNewMessage,
    replyTo,
    setReplyTo,
    chatId,
    setChatId,
    selectedDepartment,
    setSelectedDepartment,
    isSending,
    isUploading,
    departments,
    hasOnlineStaff,
    guestName,
    setGuestName,
    guestEmail,
    setGuestEmail,
    guestPhone,
    setGuestPhone,
    guestSubject,
    setGuestSubject,
    ticketForm,
    setTicketForm,
    handleFileUpload,
    handleSendMessage,
    handleChatEnd,
    attachmentPreview,
    setAttachmentPreview,
    handleSendAttachment,
    departmentAvailability,
    departmentRoles,
  } = useChat(true);

  const { data: chatData } = useQuery(
    ["guest-chat-history", chatId],
    () => (chatId ? chatService.getGuestChatHistory(chatId, guestEmail) : null),
    {
      enabled: !!chatId && !!guestEmail,
    }
  );

  const validateInputs = () => {
    let isValid = true;
    const newErrors = {
      name: "",
      email: "",
      phone: "",
      subject: "",
    };

    if (!guestName.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!guestEmail.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!emailRegex.test(guestEmail)) {
      newErrors.email = "Please enter a valid email";
      isValid = false;
    }

    const phoneDigits = guestPhone.replace(/[^0-9+]/g, "");

    if (
      guestPhone.trim() &&
      (phoneDigits.length < 7 || phoneDigits.length > 20)
    ) {
      newErrors.phone = "Please enter a valid phone number";
      isValid = false;
    }

    if (!guestSubject.trim()) {
      newErrors.subject = "Subject is required";
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGuestName(value);
    if (value.trim()) {
      setErrors((prev) => ({ ...prev, name: "" }));
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGuestEmail(value);
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (emailRegex.test(value)) {
      setErrors((prev) => ({ ...prev, email: "" }));
    }
  };

  const resumeGuestChat = async () => {
    const email = guestEmail.trim();
    if (!email || chatId) return;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return;

    try {
      const { chat } = await chatService.getGuestActiveChats(email);
      if (!chat) return;

      setGuestName(chat.guest_name || guestName);
      setGuestPhone(chat.guest_phone || guestPhone);
      setGuestSubject(chat.subject || guestSubject);
      setSelectedDepartment(chat.department || "");
      setChatId(chat.chat_id);
      setIsOpen(true);
      setIsMinimized(false);
    } catch (error) {
      console.error("Failed to resume guest chat:", error);
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGuestPhone(value);

    const phoneDigits = value.replace(/[^0-9]/g, "");

    if (phoneDigits.length < 7 || phoneDigits.length > 15) {
      setErrors((prev) => ({
        ...prev,
        phone: "Phone number must be between 7-15 digits",
      }));
    } else {
      setErrors((prev) => ({ ...prev, phone: "" }));
    }
  };

  const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGuestSubject(value);
    if (value.trim()) {
      setErrors((prev) => ({ ...prev, subject: "" }));
    }
  };
  useEffect(() => {
    if (departments?.length > 0 && !ticketForm.departments) {
      setTicketForm((prev) => ({
        ...prev,
        departments: departments[0],
      }));
    }
  }, [departments]);

  const startGuestChat = async () => {
    if (!selectedDepartment || !validateInputs()) return;

    try {
      const response = await chatService.startGuestChat({
        name: guestName,
        email: guestEmail,
        phone: guestPhone,
        subject: guestSubject,
        staff_role: selectedDepartment,
        department: selectedDepartment,
      });

      setChatId(response.chat_id);
      setIsOpen(true);
      setIsMinimized(false);

      chatService.subscribeToGuestChat(response.chat_id, guestEmail);
    } catch (error) {
      console.error("Failed to start guest chat:", error);
    }
  };

  const handleTicketSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await chatService.createGuestTicket({
        subject: ticketForm.subject,
        description: ticketForm.description,
        department_role_id: departmentRoles[selectedDepartment],
        priority: ticketForm.priority,
        guest_email: guestEmail,
        guest_name: guestName,
        phone: guestPhone,
        is_guest: true,
      });

      setTicketForm({
        subject: "",
        description: "",
        departments: departments[0] || "",
        priority: "medium",
      });

      setGuestName("");
      setGuestEmail("");
      setGuestPhone("");
      setGuestSubject("");
      setShowTicketForm(false);
      setIsOpen(false);
    } catch (error) {
      console.error("Failed to create ticket:", error);
    }
  };

  const resetGuestState = () => {
    setGuestName("");
    setGuestEmail("");
    setGuestPhone("");
    setGuestSubject("");
    setShowTicketForm(false);
    setSelectedDepartment("");
    setChatId(null);
    setTicketForm({
      subject: "",
      description: "",
      departments: "",
      priority: "medium",
    });
  };

  const handleEndChat = () => {
    handleChatEnd();
  };

  const handleCloseChat = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, CLOSE_FADE_MS);
  };

  const inputRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (chatId && !isSending) {
      inputRef.current?.focus();
    }
  }, [chatId, isSending, newMessage]);

  useEffect(() => {
    const textarea = inputRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
  }, [newMessage]);

  useEffect(() => {
    if (!chatId) return;

    const onChatMessage = (data: any) => {
      const incomingChatId = data?.chat_room?.id || data?.chatId;
      if (!incomingChatId || String(incomingChatId) !== String(chatId)) return;

      const incomingMessage = data?.message;

      if (incomingMessage?.content && typeof incomingMessage?.id === "number") {
        queryClient.setQueryData(["guest-chat-history", chatId], (oldData: any) => {
          if (!oldData) {
            return { messages: [incomingMessage] };
          }

          const existingMessages = Array.isArray(oldData.messages) ? oldData.messages : [];
          const exists = existingMessages.some((message: ChatMessage) => message.id === incomingMessage.id);
          if (exists) return oldData;

          return {
            ...oldData,
            messages: [...existingMessages, incomingMessage],
          };
        });
        return;
      }

      queryClient.invalidateQueries({ queryKey: ["guest-chat-history", chatId] });
    };

    const onChatStatus = (data: any) => {
      const statusChatId = data?.chat_room?.id || data?.chatId;
      if (!statusChatId || String(statusChatId) !== String(chatId)) return;

      queryClient.invalidateQueries({ queryKey: ["guest-chat-history", chatId] });
    };

    socketService.socketInstance?.on("chat.message", onChatMessage);
    socketService.socketInstance?.on("chat-status", onChatStatus);

    return () => {
      socketService.socketInstance?.off("chat.message", onChatMessage);
      socketService.socketInstance?.off("chat-status", onChatStatus);
    };
  }, [chatId, queryClient]);

  return (
    <div className={styles.wrapper}>
      {!isOpen ? (
        <button onClick={() => setIsOpen(true)} className={styles.openButton}>
          <ChatIcon className={styles.openButtonIcon} />
          <span className={styles.openButtonText}>Chat Support</span>
        </button>
      ) : (
        <div
          className={`${styles.chatContainer} ${isClosing ? styles.chatContainerClosing : ""}`}
        >
          <div className={styles.header}>
            <h3 className={styles.headerTitle}>Chat Support</h3>
            <div className={styles.headerActions}>
              {chatId && chatData?.chat_room?.status !== "ended" && (
                <button
                  onClick={handleEndChat}
                  className={styles.headerActionBtn}
                  title={chatData?.chat_room?.status === "pending" ? "Cancel Request" : "End Chat"}
                >
                  <ArrowRightOnRectangleIcon className={styles.iconXs} />
                  {chatData?.chat_room?.status === "pending" ? "Cancel Request" : "End Chat"}
                </button>
              )}
              {chatId && chatData?.chat_room?.status === "ended" && (
                <button
                  onClick={resetGuestState}
                  className={styles.headerActionBtn}
                  title="New Chat"
                >
                  <ChatIcon className={styles.iconXs} />
                  New Chat
                </button>
              )}
              <span className={styles.headerDivider} />
              <button
                onClick={() => setIsMinimized(!isMinimized)}
                className={styles.headerButton}
                title={isMinimized ? "Maximize" : "Minimize"}
              >
                {isMinimized ? (
                  <MaximizeIcon className={styles.iconXs} />
                ) : (
                  <MinimizeIcon className={styles.iconXs} />
                )}
              </button>
              <button
                onClick={handleCloseChat}
                className={styles.headerButton}
                title="Close"
              >
                <CloseIcon className={styles.iconXs} />
              </button>
            </div>
          </div>

          <div
            className={`${styles.collapsibleBody} ${isMinimized ? styles.collapsibleBodyCollapsed : ""}`}
          >
            <div className={styles.contentArea}>
                {!chatId ? (
                  <div className={styles.formGroup}>
                    {hasOnlineStaff ? (
                      <>
                        <div className={styles.deptWrapper}>
                          <label className={styles.label}>Department</label>
                          <select
                            value={selectedDepartment}
                            onChange={(e) => setSelectedDepartment(e.target.value)}
                            className={styles.deptSelect}
                          >
                            <option value="">Select Department</option>
                            {departments?.map((dept: string) => (
                              <option key={dept} value={dept}>
                                {dept}
                              </option>
                            ))}
                          </select>
                        </div>

                        {selectedDepartment &&
                          (departmentAvailability[selectedDepartment] ? (
                            <>
                              <div className={styles.fieldGroup}>
                                <input
                                  type="text"
                                  placeholder="Name"
                                  value={guestName}
                                  onChange={handleNameChange}
                                  className={`${styles.input} ${errors.name ? styles.inputError : ""}`}
                                />
                                {errors.name && (
                                  <p className={styles.errorText}>{errors.name}</p>
                                )}
                              </div>

                              <div className={styles.fieldGroup}>
                                <input
                                  type="email"
                                  placeholder="Email"
                                  value={guestEmail}
                                  onChange={handleEmailChange}
                                  onBlur={resumeGuestChat}
                                  className={`${styles.input} ${errors.email ? styles.inputError : ""}`}
                                />
                                {errors.email && (
                                  <p className={styles.errorText}>{errors.email}</p>
                                )}
                              </div>

                              <div className={styles.fieldGroup}>
                                <input
                                  type="tel"
                                  placeholder="Phone (e.g. +61 ### ### ###)"
                                  value={guestPhone}
                                  onChange={handlePhoneChange}
                                  pattern="[0-9+() -]*"
                                  maxLength={20}
                                  className={`${styles.input} ${errors.phone ? styles.inputError : ""}`}
                                />
                                {errors.phone && (
                                  <p className={styles.errorText}>{errors.phone}</p>
                                )}
                              </div>

                              <div className={styles.fieldGroup}>
                                <input
                                  type="text"
                                  placeholder="Subject"
                                  value={guestSubject}
                                  onChange={handleSubjectChange}
                                  className={`${styles.input} ${errors.subject ? styles.inputError : ""}`}
                                />
                                {errors.subject && (
                                  <p className={styles.errorText}>{errors.subject}</p>
                                )}
                              </div>

                              <button
                                onClick={startGuestChat}
                                disabled={!selectedDepartment || !guestName || !guestEmail}
                                className={styles.startChatBtn}
                              >
                                Start Chat
                              </button>
                            </>
                          ) : (
                            <TicketForm
                              ticketForm={ticketForm}
                              setTicketForm={setTicketForm}
                              guestFields={{
                                name: guestName,
                                email: guestEmail,
                                phone: guestPhone,
                                errors,
                                onNameChange: handleNameChange,
                                onEmailChange: handleEmailChange,
                                onEmailBlur: resumeGuestChat,
                                onPhoneChange: handlePhoneChange,
                              }}
                              onSubmit={handleTicketSubmit}
                              submitLabel="Submit Ticket"
                            />
                          ))}
                      </>
                    ) : (
                      <TicketForm
                        ticketForm={ticketForm}
                        setTicketForm={setTicketForm}
                        departments={departments}
                        selectedDepartment={selectedDepartment}
                        onDepartmentChange={setSelectedDepartment}
                        showDepartmentSelect
                        guestFields={{
                          name: guestName,
                          email: guestEmail,
                          phone: guestPhone,
                          errors,
                          onNameChange: handleNameChange,
                          onEmailChange: handleEmailChange,
                          onEmailBlur: resumeGuestChat,
                          onPhoneChange: handlePhoneChange,
                        }}
                        onSubmit={handleTicketSubmit}
                        submitLabel="Submit Ticket"
                      />
                    )}
                  </div>
                ) : (
                  <MessageList
                    messages={chatData?.messages}
                    guestEmail={guestEmail}
                    isGuest={true}
                    chatRoom={chatData?.chat_room}
                    chatStatus={chatData?.chat_room?.status}
                    onReply={setReplyTo}
                  />
                )}
              </div>

              {chatId && attachmentPreview && (
                <div className={styles.attachPreviewArea}>
                  <div className={styles.attachPreviewHeader}>
                    <span className={styles.attachPreviewLabel}>Attachment Preview</span>
                    <button
                      onClick={() => setAttachmentPreview(null)}
                      className={styles.removeAttachBtn}
                    >
                      <XCircleIcon className={styles.iconSm} />
                    </button>
                  </div>
                  <div className={styles.attachPreviewImgWrap}>
                    <img
                      src={attachmentPreview.preview}
                      alt="Preview"
                      className={styles.attachPreviewImg}
                    />
                  </div>
                </div>
              )}
              {chatId && replyTo && (
                <div className={styles.replyBanner}>
                  <div className={styles.replyText}>
                    Replying to{" "}
                    <span className={styles.replyAuthor}>
                      {replyTo.content.metadata?.guest_name ||
                        replyTo.sender?.name ||
                        "message"}
                    </span>
                    : {replyTo.content.body}
                  </div>
                  <button
                    onClick={() => setReplyTo(null)}
                    className={styles.replyCloseBtn}
                  >
                    <XMarkIcon className={styles.iconXs} />
                  </button>
                </div>
              )}
              {chatId && (
                <div className={styles.inputArea}>
                  <div className={styles.inputWrapper}>
                    <textarea
                      ref={inputRef}
                      rows={1}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          if (attachmentPreview) {
                            handleSendAttachment();
                          } else {
                            handleSendMessage();
                          }
                        }
                      }}
                      placeholder="Type your message..."
                      className={styles.messageInput}
                      disabled={isSending || isUploading}
                    />
                    <input
                      type="file"
                      id="guest-file-upload"
                      className={styles.fileInput}
                      onChange={(e) => e.target.files && handleFileUpload(e.target.files)}
                      accept="image/*"
                    />
                    <button
                      onClick={() =>
                        document.getElementById("guest-file-upload")?.click()
                      }
                      className={styles.attachBtn}
                      disabled={isUploading}
                    >
                      {isUploading ? (
                        <span className="animate-pulse">⏳</span>
                      ) : (
                        <AttachmentIcon className={styles.iconSm} />
                      )}
                    </button>
                    <button
                      onClick={() =>
                        attachmentPreview ? handleSendAttachment() : handleSendMessage()
                      }
                      className={styles.sendBtn}
                      disabled={isSending || isUploading}
                    >
                      {isSending ? (
                        <span className="animate-pulse">⏳</span>
                      ) : (
                        <PaperAirplaneIcon className={styles.iconSm} />
                      )}
                    </button>
                  </div>
                </div>
              )}
          </div>
        </div>
      )}
    </div>
  );
}
